import React from 'react';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Settings } from 'lucide-react';

interface HybridBarChartProps {
  first12hMwh: number;
  second12hMwh: number;
  dayLabel: string;
  height?: number;
  onConfigure?: () => void;
  /**
   * One solid colour for both blocks. The dashboard passes its hybrid green,
   * because there every hybrid quantity is green; the bid screen keeps its own.
   */
  color?: string;
}

const CustomBarTooltip = ({ active, payload, label, color }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-2xl border border-gray-100 bg-white/95 p-3.5 shadow-xl backdrop-blur-sm">
        <p className="mb-1 text-xs font-bold text-gray-900">{label}</p>
        <div className="flex items-center gap-2 text-xs">
          <div className="h-2.5 w-2.5 rounded-full bg-indigo-600" style={color ? { backgroundColor: color } : undefined} />
          <span className="text-gray-600">Υβριδική Ενέργεια:</span>
          <span className="font-bold text-indigo-950">{payload[0].value} MWh</span>
        </div>
      </div>
    );
  }
  return null;
};

export const HybridBarChart: React.FC<HybridBarChartProps> = ({
  first12hMwh,
  second12hMwh,
  dayLabel,
  height = 260,
  onConfigure,
  color,
}) => {
  const data = [
    {
      name: '1ο 12ωρο (00:00 - 11:59)',
      shortName: '1ο 12ωρο',
      mwh: first12hMwh,
    },
    {
      name: '2ο 12ωρο (12:00 - 23:59)',
      shortName: '2ο 12ωρο',
      mwh: second12hMwh,
    },
  ];

  const totalHybridDayMwh = first12hMwh + second12hMwh;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="h-full"
    >
      <div className="flex h-full flex-col justify-between rounded-3xl border border-gray-100/80 bg-white p-6 shadow-xl shadow-gray-900/5 transition-all duration-300 hover:shadow-2xl hover:shadow-gray-900/10">
        {/* Header Block */}
        <div className="mb-4 space-y-3">
          {/* Top Row: Title + Configure Button */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="truncate text-base font-bold text-gray-900 sm:text-lg">
                  Δήλωση Υβριδικής (1ο & 2ο 12ωρο)
                </h3>
                <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-bold text-indigo-800">
                  Σύνολο: {totalHybridDayMwh} MWh
                </span>
              </div>
              <p className="mt-0.5 text-xs font-medium text-gray-500">
                Ενέργεια Υβριδικής Μονάδας ανά 12ωρο – {dayLabel}
              </p>
            </div>

            {onConfigure && (
              <button
                onClick={onConfigure}
                className="flex-shrink-0 rounded-xl border border-gray-200 bg-gray-50/80 p-2 text-gray-500 shadow-sm transition-all duration-200 hover:bg-white hover:text-indigo-600 hover:shadow-md"
                title="Ρύθμιση Γραφήματος"
              >
                <Settings className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Series Legends Row */}
          <div className="flex flex-wrap items-center gap-4 border-t border-gray-100/80 pt-2.5">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
              <span
                className="h-2.5 w-2.5 rounded-full bg-indigo-600"
                style={color ? { backgroundColor: color } : undefined}
              />
              <span>Υβριδική Προσφορά (MWh)</span>
            </div>
            <div className="text-xs text-gray-400">
              (1 μπάρα ανά 12ωρο)
            </div>
          </div>
        </div>

        {/* Bar Chart Area */}
        <div style={{ height }} className="w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
              barSize={56}
            >
              <defs>
                <linearGradient id="barGradient1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={1} />
                  <stop offset="100%" stopColor="#4f46e5" stopOpacity={0.8} />
                </linearGradient>
                <linearGradient id="barGradient2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity={1} />
                  <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.8} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }}
                dy={6}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748b', fontSize: 11 }}
                unit=" MWh"
              />
              <Tooltip content={<CustomBarTooltip color={color} />} />
              <Bar dataKey="mwh" radius={[12, 12, 0, 0]}>
                <Cell fill={color ?? 'url(#barGradient1)'} />
                <Cell fill={color ?? 'url(#barGradient2)'} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </motion.div>
  );
};

export default HybridBarChart;
